import { NyuLineProperties } from '@shared/types'

type BarShapeProps = {
    barShapeFill?: string
    barShapeStroke?: string
    barShapeStrokeWidth?: number
}

const removePrefix = ({
    barShapeStrokeWidth,
    barShapeStroke,
    barShapeFill,
}: BarShapeProps) => {
    return {
        strokeWidth: barShapeStrokeWidth,
        stroke: barShapeStroke,
        fill: barShapeFill,
    }
}

function Circle(props: BarShapeProps) {
    return <circle {...removePrefix(props)} cx={50} cy={50} r={50} />
}

function Square(props: BarShapeProps) {
    return <rect {...removePrefix(props)} width={100} height={100} />
}

const crossPath =
    'M57.5 2.5V42.5H97.5V57.5H57.5V97.5H42.5V57.5H2.5V42.5H42.5V2.5H57.5Z'

function Cross(props: BarShapeProps) {
    return <path {...removePrefix(props)} d={crossPath} />
}

// The cross turned onto its diagonals. Arms stay as long as the cross' arms, so
// the two add up to an asterisk when they are drawn on the same value.
function XCross(props: BarShapeProps) {
    return (
        <path
            {...removePrefix(props)}
            d={crossPath}
            transform="rotate(45 50 50)"
        />
    )
}

function Diamond(props: BarShapeProps) {
    return (
        <rect
            {...removePrefix(props)}
            x={50}
            y={0}
            width={100 / Math.sqrt(2)}
            height={100 / Math.sqrt(2)}
            transform="rotate(45 50 0)"
        />
    )
}

function Triangle(props: BarShapeProps) {
    return (
        <path
            {...removePrefix(props)}
            d="M86.7291 75.5449H5.27109L45.9996 4.99902L86.7291 75.5449Z"
        />
    )
}

const shapes = {
    circle: Circle,
    square: Square,
    cross: Cross,
    xcross: XCross,
    diamond: Diamond,
    triangle: Triangle,
}

type ShapeProps = NyuLineProperties & {
    x?: number
    y?: number
    size?: number
}

const isTransparent = (fill?: string) =>
    fill === 'none' || fill === 'transparent'

export function Shape({
    x = 0,
    y = 0,
    size = 100,
    barShapeType,
    ...rest
}: ShapeProps) {
    const Component = shapes[barShapeType!]
    return (
        <g transform={`translate(${x}, ${y}) scale(${size / 100})`}>
            <Component {...rest} />

            {/* Draw shape on top a second time without stroke to ensure the stroke is outside.
                A transparent shape has nothing to draw with, and the second pass would only
                hide whatever the shape is supposed to let through. */}
            {rest.barShapeStrokeWidth && !isTransparent(rest.barShapeFill) && (
                <Component
                    {...rest}
                    barShapeStrokeWidth={0}
                    barShapeStroke="none"
                />
            )}
        </g>
    )
}
